import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import '../pages/ABTestsPage.css';

/* ============================================================
   HELPERS
   ============================================================ */

/** Deterministic multi-line chart for variants — pure SVG, no lib */
function genVariantChart(variants, status) {
  const W = 492, H = 80, N = 10;

  // LCG seeded by variant count + status
  let s = (variants.length || 2) * 7 + (status === 'running' ? 11 : status === 'winner' ? 17 : 5);
  const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };

  const colors = ['#ff5436', '#8b9b3f', '#e8b94a', '#a8a8af'];

  const lines = (variants.length ? variants : [{ url: 'A' }, { url: 'B' }]).map((v, vi) => {
    let y = H * 0.6;
    const pts = [];
    const trend = status === 'draft' ? 0 : vi === 0 ? -0.08 : -0.04;
    for (let i = 0; i < N; i++) {
      y = Math.max(6, Math.min(H - 6, y + (rand() - 0.5 + trend) * 14));
      pts.push([(i / (N - 1)) * W, y]);
    }
    const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    return { d, color: colors[vi % colors.length] };
  });

  return lines;
}

function formatNum(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ============================================================
   VARIANT CARD
   ============================================================ */
function VariantCard({ variant, index, totalClicks, isWinner }) {
  const clicks = variant.clicks || 0;
  const pct = totalClicks > 0 ? ((clicks / totalClicks) * 100).toFixed(1) : 0;
  const lift = variant.lift != null ? variant.lift : null;
  const conf = variant.confidence != null ? variant.confidence : null;

  return (
    <div className={`ab-drawer-variant${isWinner ? ' winner-variant' : ''}`}>
      <div className="ab-drawer-variant-header">
        <div className="ab-drawer-variant-label">
          Variant {String.fromCharCode(65 + index)}
          {isWinner && <span className="ab-drawer-variant-badge">Winner</span>}
        </div>
        {variant.weight != null && (
          <span style={{ fontFamily: "'Geist Mono',monospace", fontSize: 10, color: 'var(--ink-faint)' }}>
            {variant.weight}% weight
          </span>
        )}
      </div>
      <div className="ab-drawer-variant-url">{variant.url || '—'}</div>
      <div className="ab-drawer-variant-stats">
        <div className="ab-drawer-variant-stat">
          <div className="ab-drawer-variant-stat-val">{formatNum(clicks)}</div>
          <div className="ab-drawer-variant-stat-lbl">Clicks</div>
        </div>
        <div className="ab-drawer-variant-stat">
          <div className={`ab-drawer-variant-stat-val${lift != null && lift > 0 ? ' olive' : ''}`}>
            {lift != null ? `${lift > 0 ? '+' : ''}${lift.toFixed(1)}%` : '—'}
          </div>
          <div className="ab-drawer-variant-stat-lbl">Lift</div>
        </div>
        <div className="ab-drawer-variant-stat">
          <div className="ab-drawer-variant-stat-val">
            {conf != null ? `${conf.toFixed(0)}%` : '—'}
          </div>
          <div className="ab-drawer-variant-stat-lbl">Confidence</div>
        </div>
      </div>
      <div className="ab-drawer-bar-wrap">
        <div className="ab-drawer-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 10, color: 'var(--ink-faint)', marginTop: 5, textAlign: 'right' }}>
        {pct}% traffic
      </div>
    </div>
  );
}

/* ============================================================
   MAIN DRAWER
   ============================================================ */
export default function ABTestDrawer({ test, onClose }) {
  const navigate = useNavigate();
  const open = !!test;

  // ESC key handled by Radix Dialog natively

  const handleNavigate = useCallback(() => {
    if (test?._linkId) {
      onClose();
      navigate(`/links/${test._linkId}`);
    }
  }, [test, onClose, navigate]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  if (!test) return null;

  const ab = test.ab || {};
  const variants = ab.variants || [];
  const totalClicks = test.traffic || 0;
  const winnerIndex = ab.winner_index ?? null;
  const chartLines = genVariantChart(variants, test.status);

  // ---- KPI values ----
  const bestLift = variants.reduce((mx, v) => {
    const l = v.lift;
    return l != null && !isNaN(l) && l > mx ? l : mx;
  }, -Infinity);
  const bestLiftStr = isFinite(bestLift) ? `${bestLift > 0 ? '+' : ''}${bestLift.toFixed(1)}%` : '—';

  const maxConf = variants.reduce((mx, v) => {
    const c = v.confidence;
    return c != null && !isNaN(c) && c > mx ? c : mx;
  }, -Infinity);
  const maxConfStr = isFinite(maxConf) ? `${maxConf.toFixed(0)}%` : '—';

  const createdAt = ab.created_at || test.ab?.created_at;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }} modal>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay className="ab-drawer-backdrop" />

        {/* Panel */}
        <Dialog.Content
          className="ab-drawer"
          aria-label={`${test.slug} A/B test detail`}
          onInteractOutside={onClose}
        >
          {/* ── Header ── */}
          <div className="ab-drawer-header">
            <div className="ab-drawer-header-top">
              {/* Status pill with inline pulsing dot */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: test.status === 'running' ? 'var(--olive)'
                      : test.status === 'winner' ? 'var(--coral)'
                      : 'var(--mustard)',
                    animation: test.status === 'running' ? 'ab-dot-pulse 1.5s infinite' : 'none',
                    flexShrink: 0,
                  }}
                />
                <span style={{
                  fontFamily: "'Geist Mono',monospace", fontSize: 11,
                  color: test.status === 'running' ? 'var(--olive)'
                    : test.status === 'winner' ? 'var(--coral-deep)'
                    : '#b08820',
                  fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>
                  {test.status}
                </span>
              </div>

              <Dialog.Close asChild>
                <button type="button" className="ab-drawer-close" aria-label="Close drawer">✕</button>
              </Dialog.Close>
            </div>

            <Dialog.Title className="ab-drawer-title">
              {test.slug} <em>·</em> test
            </Dialog.Title>

            <div className="ab-drawer-meta">
              <span>{test.variantCount} variants</span>
              <span className="ab-drawer-meta-dot">·</span>
              <span>{formatNum(totalClicks)} total clicks</span>
              {createdAt && (
                <>
                  <span className="ab-drawer-meta-dot">·</span>
                  <span>Started {fmtDate(createdAt)}</span>
                </>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          <div className="ab-drawer-body">
            {/* KPIs */}
            <div className="ab-drawer-kpis">
              <div className="ab-drawer-kpi">
                <div className="ab-drawer-kpi-label">Traffic</div>
                <div className="ab-drawer-kpi-value">{formatNum(totalClicks)}</div>
                <div className="ab-drawer-kpi-meta">total clicks</div>
              </div>
              <div className="ab-drawer-kpi">
                <div className="ab-drawer-kpi-label">Best Lift</div>
                <div className={`ab-drawer-kpi-value${isFinite(bestLift) && bestLift > 0 ? ' olive' : ''}`}>
                  {bestLiftStr}
                </div>
                <div className="ab-drawer-kpi-meta">vs control</div>
              </div>
              <div className="ab-drawer-kpi">
                <div className="ab-drawer-kpi-label">Confidence</div>
                <div className="ab-drawer-kpi-value">{maxConfStr}</div>
                <div className="ab-drawer-kpi-meta">statistical</div>
              </div>
            </div>

            {/* Mini chart */}
            <div className="ab-drawer-chart-wrap">
              <div className="ab-drawer-chart-label">Click trend by variant</div>
              <svg className="ab-drawer-chart-svg" viewBox={`0 0 492 80`} preserveAspectRatio="none">
                {/* Horizontal guide lines */}
                {[20, 40, 60].map((y) => (
                  <line key={y} x1="0" y1={y} x2="492" y2={y} stroke="var(--line)" strokeWidth="1" />
                ))}
                {/* Variant lines */}
                {chartLines.map(({ d, color }, i) => (
                  <path key={i} d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                ))}
              </svg>
              {/* Legend */}
              <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
                {(variants.length ? variants : [{ url: 'A' }, { url: 'B' }]).map((v, i) => {
                  const cols = ['#ff5436', '#8b9b3f', '#e8b94a', '#a8a8af'];
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 14, height: 3, borderRadius: 2, background: cols[i % cols.length], display: 'inline-block' }} />
                      <span style={{ fontFamily: "'Geist Mono',monospace", fontSize: 10, color: 'var(--ink-faint)' }}>
                        {String.fromCharCode(65 + i)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Variants */}
            <div className="ab-drawer-section-title">Variants</div>
            <div className="ab-drawer-variants">
              {variants.length > 0
                ? variants.map((v, i) => (
                    <VariantCard
                      key={i}
                      variant={v}
                      index={i}
                      totalClicks={totalClicks}
                      isWinner={winnerIndex === i || v.is_winner === true}
                    />
                  ))
                : (
                  <div style={{
                    padding: '20px 16px',
                    textAlign: 'center',
                    background: 'var(--bg-warm)',
                    border: '1px solid var(--line)',
                    borderRadius: 10,
                    fontFamily: "'Geist Mono',monospace",
                    fontSize: 12,
                    color: 'var(--ink-faint)',
                  }}>
                    No variant data yet — traffic will appear once the test is running.
                  </div>
                )}
            </div>

            {/* Destination */}
            {test.dest && (
              <>
                <div className="ab-drawer-section-title" style={{ marginTop: 4 }}>Destination URL</div>
                <div style={{
                  fontFamily: "'Geist Mono',monospace", fontSize: 11.5,
                  color: 'var(--ink-dim)', wordBreak: 'break-all',
                  background: 'var(--bg-warm)', border: '1px solid var(--line)',
                  borderRadius: 8, padding: '10px 12px',
                }}>
                  {test.dest}
                </div>
              </>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="ab-drawer-footer">
            {/* Status-conditional primary + secondary actions */}
            {test.status === 'running' && (
              <>
                {/* TODO(ab-drawer): wire to abTestApi.declareWinner when RPC is confirmed */}
                <button type="button" className="btn-coral" disabled title="Declare the winning variant">
                  Apply winner permanently
                </button>
                {/* TODO(ab-drawer): wire to abTestApi.update({ enabled: false }) */}
                <button type="button" className="btn-outline" disabled>
                  Pause
                </button>
              </>
            )}
            {test.status === 'winner' && (
              <>
                {/* TODO(ab-drawer): wire to abTestApi.declareWinner */}
                <button type="button" className="btn-coral" disabled title="Already declared">
                  Apply winner permanently
                </button>
                {/* TODO(ab-drawer): duplicate test logic */}
                <button type="button" className="btn-outline" disabled>
                  Duplicate
                </button>
              </>
            )}
            {test.status === 'draft' && (
              <>
                {/* TODO(ab-drawer): wire to abTestApi.update({ enabled: true }) */}
                <button type="button" className="btn-coral" disabled>
                  Launch test
                </button>
              </>
            )}

            {/* Always-available analytics link */}
            <button
              type="button"
              className="btn-outline"
              style={{ marginLeft: 'auto' }}
              onClick={handleNavigate}
              disabled={!test._linkId}
            >
              Open full analytics →
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
